import { Button } from '@/ui/Button';
import { useCompletarLeccion, type CursoDetalle, type Leccion as DatosDeLeccion } from './api';

/**
 * Leccion — el reproductor de una lección: un vídeo de YouTube o un PDF.
 *
 * ## Solo el identificador del vídeo, y saneado
 *
 * La URL la escribe quien gestiona el curso. De ella se saca **solo el
 * identificador** —once caracteres de `[A-Za-z0-9_-]`— y se compone la dirección
 * del `embed` a partir de él. Nunca se mete la URL entera en el `src` del
 * iframe: una URL cualquiera ahí es un iframe a cualquier sitio.
 *
 * ## Completar no repinta todo
 *
 * El vanilla volvía a pedir el curso, repintaba la pantalla entera y volvía a
 * abrir la lección para reflejar el avance. Aquí la mutación invalida el curso y
 * React actualiza lo que cambió: la marca de la lección, la barra y el pie del
 * temario.
 */

const ID_DE_YOUTUBE = /(?:v=|youtu\.be\/|embed\/)([\w-]{6,20})/;

export function Leccion({
  curso, leccion, onReportar,
}: {
  curso: CursoDetalle;
  leccion: DatosDeLeccion;
  onReportar: () => void;
}) {
  const completar = useCompletarLeccion();

  function marcarCompletada() {
    completar.mutate(
      { cursoId: curso.id, leccionId: leccion.id },
      {
        onSuccess: (r) => {
          if (r.lessons_done) {
            Utils.confetti?.();
            Utils.toast(
              r.has_quiz
                ? 'Completaste todas las lecciones. Presenta el quiz final para obtener tu certificado.'
                : r.certificate_issued
                  ? 'Curso completado. Tu certificado ya está disponible.'
                  : 'Curso completado al 100 %.',
              'success',
              5000,
            );
          } else {
            Utils.toast(`Lección completada · ${Math.round(r.progress_pct)} % del curso`, 'success');
          }
        },
        onError: (err) =>
          Utils.toast(err instanceof Error ? err.message : 'No se pudo marcar la lección', 'error'),
      },
    );
  }

  return (
    <article aria-labelledby={`leccion-${leccion.id}`} className="overflow-hidden rounded-md border border-line bg-surface">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-3">
        <h2 id={`leccion-${leccion.id}`} className="min-w-0 text-4 font-bold text-brand-ink [overflow-wrap:anywhere]">
          {leccion.title}
        </h2>
        {leccion.completed ? (
          <span className="flex items-center gap-1.5 text-3 text-ok-ink">
            <i className="bi bi-check-circle-fill" aria-hidden="true" /> Completada
          </span>
        ) : (
          <Button size="sm" loading={completar.isPending} onClick={marcarCompletada}>
            <i className="bi bi-check-lg" aria-hidden="true" /> Marcar como completada
          </Button>
        )}
      </header>

      <Contenido leccion={leccion} />

      {leccion.description && (
        <p className="border-t border-line px-4 py-3 text-3 text-ink-2 [overflow-wrap:anywhere]">
          {leccion.description}
        </p>
      )}

      <div className="flex justify-end border-t border-line px-2 py-1.5">
        <Button variant="ghost" size="sm" onClick={onReportar}>
          <i className="bi bi-exclamation-triangle" aria-hidden="true" /> Reportar un problema con esta lección
        </Button>
      </div>
    </article>
  );
}

function Contenido({ leccion }: { leccion: DatosDeLeccion }) {
  if (leccion.video_type === 'youtube' && leccion.video_url) {
    const id = leccion.video_url.match(ID_DE_YOUTUBE)?.[1];
    if (!id) {
      return (
        <p role="alert" className="m-4 rounded-sm border border-line bg-sunken p-3 text-3 text-ink-2">
          <i className="bi bi-exclamation-triangle mr-1.5 text-warn-ink" aria-hidden="true" />
          La dirección del vídeo no parece de YouTube. Repórtalo con el botón de abajo.
        </p>
      );
    }
    return (
      <iframe
        src={`https://www.youtube.com/embed/${encodeURIComponent(id)}?rel=0`}
        title={leccion.title}
        className="block aspect-video w-full border-0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
        allowFullScreen
      />
    );
  }

  if (leccion.video_type === 'pdf' && leccion.pdf_url) {
    return (
      <div>
        <div className="flex items-center gap-2 border-b border-line bg-sunken px-4 py-2 text-2 text-ink-2">
          <i className="bi bi-file-earmark-pdf" aria-hidden="true" />
          <span className="min-w-0 flex-1 truncate">{leccion.title}</span>
          <a
            href={leccion.pdf_url}
            target="_blank"
            rel="noopener"
            className="inline-flex min-h-8 items-center gap-1 rounded-sm px-2 font-semibold text-brand-ink
                       no-underline hover:bg-surface pointer-coarse:min-h-11 focus-visible:focus-anillo"
          >
            <i className="bi bi-download" aria-hidden="true" /> Descargar
          </a>
        </div>
        {/* Alto fijo en escritorio, y proporción en el teléfono: 600 px en una
            pantalla de 667 dejaban el PDF sin sitio para desplazarse. */}
        <iframe
          src={leccion.pdf_url}
          title={leccion.title}
          className="block h-[70vh] min-h-96 w-full border-0 lg:h-[600px]"
        />
      </div>
    );
  }

  return (
    <p className="px-4 py-12 text-center text-3 text-muted">
      El contenido de esta lección aún no está disponible.
    </p>
  );
}
